import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Row, Col, Input, message, Popconfirm, Checkbox, Spin, Divider } from 'antd';
import { 
  PlusOutlined, 
  DeleteOutlined, 
  EditOutlined,
  UserOutlined,
  SaveOutlined,
  CloseOutlined,
  TeamOutlined,
  MailOutlined,
  LockOutlined
} from '@ant-design/icons';
import axios from 'axios';

const MobileUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '', first_name: '', last_name: '', email: '', password: '',
    dashboard: true, media: true, playlists: true, video_editor: true,
    animation_editor: false, box_management: false, // NOVE PERMISIJE
    machines: false, mappings: false, telegram: false, users: false
  });

  const USERS_URL = '/api/users/manage/';

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(USERS_URL);
      setUsers(res.data);
    } catch (error) {
      message.error('Error loading users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openOverlay = async (user = null) => {
    if (user) {
      setEditingUser(user);
      setModalLoading(true);
      setIsOverlayOpen(true);
      try {
        const response = await axios.get(`${USERS_URL}${user.id}/`);
        const u = response.data;
        setFormData({
          username: u.username,
          first_name: u.first_name || '',
          last_name: u.last_name || '',
          email: u.email || '',
          password: '',
          ...u.perms
        });
      } catch (error) {
        message.error('Error fetching details');
        setIsOverlayOpen(false);
      } finally {
        setModalLoading(false);
      }
    } else {
      setEditingUser(null);
      setFormData({
        username: '', first_name: '', last_name: '', email: '', password: '',
        dashboard: true, media: true, playlists: true, video_editor: true,
        animation_editor: false, box_management: false, // RESET NOVIH
        machines: false, mappings: false, telegram: false, users: false
      });
      setIsOverlayOpen(true);
    }
  };

  const handleSave = async () => {
    if (!formData.username || (!editingUser && !formData.password)) {
      message.warning('Username and Password are required');
      return;
    }

    const payload = {
      id: editingUser?.id,
      username: formData.username,
      password: formData.password,
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      perms: {
        dashboard: formData.dashboard,
        media: formData.media,
        playlists: formData.playlists,
        video_editor: formData.video_editor,
        animation_editor: formData.animation_editor, // SEND NEW
        box_management: formData.box_management,   // SEND NEW
        machines: formData.machines,
        mappings: formData.mappings,
        telegram: formData.telegram,
        users: formData.users
      }
    };

    try {
      if (editingUser) {
        await axios.put(USERS_URL, payload);
        message.success('User updated');
      } else {
        await axios.post(USERS_URL, payload);
        message.success('User created');
      }
      setIsOverlayOpen(false);
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.error || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/users/delete/${id}/`);
      message.success('User removed');
      fetchData();
    } catch (error) {
      message.error('Error deleting user');
    }
  };

  const columns = [
    {
      title: 'Users',
      key: 'user_card',
      render: (record) => (
        <div className="m-user-card">
          <div className="m-user-info">
            <div className="m-user-main">
              <UserOutlined style={{ color: '#3b82f6' }} />
              <span className="m-user-name">{record.username}</span>
            </div>
            <div className="m-user-role">
                {record.is_superuser ? 
                    <span style={{ color: '#fbbf24', fontSize: '10px', fontWeight: 'bold' }}>ADMIN</span> : 
                    <span style={{ color: '#3b82f6', fontSize: '10px', fontWeight: 'bold' }}>OPERATOR</span>
                }
            </div>
          </div>
          
          <div className="m-user-details">
            <div style={{ color: '#94a3b8', fontSize: '11px' }}>{record.first_name} {record.last_name}</div>
            <div style={{ color: '#64748b', fontSize: '11px' }}>{record.email || 'No email'}</div>
          </div>

          <div className="m-user-actions">
            <Button size="small" icon={<EditOutlined />} onClick={() => openOverlay(record)} style={{ background: '#1e293b', color: '#60a5fa', border: '1px solid #334155' }}>Edit</Button>
            {!record.is_superuser && (
              <Popconfirm title="Delete user?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
                <Button size="small" icon={<DeleteOutlined />} style={{ background: '#1e293b', color: '#ef4444', border: '1px solid #334155' }}>Delete</Button>
              </Popconfirm>
            )}
          </div>
        </div>
      )
    }
  ];

  return (
    <div>
      <Row gutter={[8, 8]} style={{ marginBottom: '15px' }}>
        <Col span={12}>
          <div className="st-box" style={{ borderBottomColor: '#3b82f6' }}>
            <Space>
              <TeamOutlined style={{ color: '#3b82f6' }} />
              <Space>
                <span style={{ color: '#64748b', fontSize: '11px' }}>TOTAL:</span>
                <span style={{ color: 'white', fontWeight: 'bold' }}>{users.length}</span>
              </Space>
            </Space>
          </div>
        </Col>
        <Col span={12}>
          <div className="st-box" style={{ borderBottomColor: '#3b82f6', cursor: 'pointer', padding: '0' }} onClick={() => openOverlay()}>
            <Button block type="dashed" icon={<PlusOutlined />} style={{ background: 'transparent', color: '#3b82f6', border: 'none', height: '45px', fontWeight: 'bold' }}>
              ADD USER
            </Button>
          </div>
        </Col>
      </Row>

      <Table dataSource={users} columns={columns} rowKey="id" loading={loading} pagination={false} className="m-user-table" />

      {isOverlayOpen && (
        <div className="m-custom-overlay">
          <div className="m-overlay-content">
            <div className="m-overlay-header">
              <span>{editingUser ? 'EDIT OPERATOR' : 'ADD NEW OPERATOR'}</span>
              <Button type="text" icon={<CloseOutlined style={{ color: 'white' }} />} onClick={() => setIsOverlayOpen(false)} />
            </div>
            <div className="m-overlay-body">
              <Spin spinning={modalLoading}>
                <div style={{ marginBottom: '12px' }}>
                  <label className="m-label">USERNAME</label>
                  <Input 
                    disabled={!!editingUser} 
                    value={formData.username} 
                    onChange={e => setFormData({...formData, username: e.target.value})} 
                    style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }} 
                  />
                </div>

                <Row gutter={8} style={{ marginBottom: '12px' }}>
                  <Col span={12}>
                    <label className="m-label">FIRST NAME</label>
                    <Input value={formData.first_name} onChange={e => setFormData({...formData, first_name: e.target.value})} style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }} />
                  </Col>
                  <Col span={12}>
                    <label className="m-label">LAST NAME</label>
                    <Input value={formData.last_name} onChange={e => setFormData({...formData, last_name: e.target.value})} style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }} />
                  </Col>
                </Row>

                <div style={{ marginBottom: '12px' }}>
                  <label className="m-label">EMAIL</label>
                  <Input prefix={<MailOutlined />} value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }} />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label className="m-label">{editingUser ? "NEW PASSWORD (OPTIONAL)" : "PASSWORD"}</label>
                  <Input.Password prefix={<LockOutlined />} value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }} />
                </div>

                <Divider orientation="left" style={{ borderColor: '#334155', color: '#3b82f6', fontSize: '10px', margin: '10px 0' }}>PERMISSIONS</Divider>

                <div className="m-perms-grid">
                    <Checkbox checked={formData.dashboard} onChange={e => setFormData({...formData, dashboard: e.target.checked})}>Dashboard</Checkbox>
                    <Checkbox checked={formData.media} onChange={e => setFormData({...formData, media: e.target.checked})}>Media</Checkbox>
                    <Checkbox checked={formData.playlists} onChange={e => setFormData({...formData, playlists: e.target.checked})}>Playlists</Checkbox>
                    <Checkbox checked={formData.video_editor} onChange={e => setFormData({...formData, video_editor: e.target.checked})}>Editor</Checkbox>
                    <Checkbox checked={formData.animation_editor} onChange={e => setFormData({...formData, animation_editor: e.target.checked})}>Anim Editor</Checkbox>
                    <Checkbox checked={formData.box_management} onChange={e => setFormData({...formData, box_management: e.target.checked})}>Box Mng</Checkbox>
                    <Checkbox checked={formData.machines} onChange={e => setFormData({...formData, machines: e.target.checked})}>Machines</Checkbox>
                    <Checkbox checked={formData.mappings} onChange={e => setFormData({...formData, mappings: e.target.checked})}>Mappings</Checkbox>
                    <Checkbox checked={formData.telegram} onChange={e => setFormData({...formData, telegram: e.target.checked})}>Telegram</Checkbox>
                    <Checkbox checked={formData.users} onChange={e => setFormData({...formData, users: e.target.checked})}>Users</Checkbox>
                </div>

                <Button block type="primary" icon={<SaveOutlined />} onClick={handleSave} style={{ background: '#3b82f6', height: '45px', fontWeight: 'bold', marginTop: '20px' }}>
                  SAVE OPERATOR
                </Button>
              </Spin>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .st-box { background: #1e293b; padding: 12px; border-radius: 8px; border: 1px solid #334155; border-bottom: 3px solid; display: flex; justify-content: center; align-items: center; }
        .m-user-table .ant-table { background: transparent !important; }
        .m-user-table .ant-table-thead { display: none !important; }
        .m-user-table .ant-table-tbody > tr > td { border: none !important; padding: 0 0 12px 0 !important; }
        .m-user-table .ant-table-tbody > tr:hover > td { background: transparent !important; }
        .m-user-card { background: #1e293b; border-radius: 8px; border: 1px solid #334155; padding: 12px; }
        .m-user-info { display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px; }
        .m-user-main { display: flex; align-items: center; gap: 10px; }
        .m-user-name { color: white; font-weight: bold; font-size: 14px; }
        .m-user-details { margin-bottom: 12px; padding-left: 24px; }
        .m-user-actions { display: flex; gap: 8px; justify-content: flex-end; border-top: 1px solid #1e293b; padding-top: 10px; }
        .m-custom-overlay { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(0,0,0,0.85); z-index: 9999; display: flex; align-items: center; justify-content: center; }
        .m-overlay-content { background: #1e293b; width: 85%; max-width: 400px; border-radius: 12px; border: 1px solid #334155; overflow: hidden; max-height: 90vh; overflow-y: auto; }
        .m-overlay-header { padding: 15px; border-bottom: 1px solid #334155; display: flex; justify-content: space-between; align-items: center; color: white; font-weight: bold; font-size: 14px; }
        .m-overlay-body { padding: 20px; }
        .m-label { color: #64748b; font-size: 10px; font-weight: bold; display: block; margin-bottom: 5px; }
        .m-perms-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .m-perms-grid .ant-checkbox-wrapper { color: #94a3b8; font-size: 11px; }
        .ant-checkbox-wrapper + .ant-checkbox-wrapper { margin-left: 0 !important; }
      `}</style>
    </div>
  );
};

export default MobileUsers;